import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderIcon from "@mui/icons-material/FolderOpenOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { tokens } from "../../theme";
import { describeError, useToast } from "../../components/toast";
import {
  ownerBatchApi,
  type BatchView,
  type IngestReadiness,
  type OwnerShell,
} from "../../http/foyer";
import { uploadInBlocks } from "../../lib/blockUpload";
import { PageHeader } from "../../components/PageHeader";
import { ItemFormSurface } from "../../components/EditDrawer";
import { packageFindingText } from "../../components/distribution/findings";
import VodWizardDialog from "../../components/distribution/VodWizardDialog";
import OwnerFeedSelect, {
  feedChoiceReady,
  useOwnerFeeds,
} from "../../components/supplier/OwnerFeedSelect";

/**
 * Upload — a supplier delivering their own material (`CONTENT-9`, `BE-36`).
 *
 * ## Why this page had to exist before the menu could grow
 *
 * "My titles" told a studio, verbatim, to *"send new material to your Nenda
 * contact"* — while the owner-scoped batch routes had been registered the whole
 * time and nothing in the client bound them. A partner-facing product whose
 * first instruction is "email somebody" is a product that has not shipped.
 *
 * ## The same handlers as staff bulk ingest, and deliberately fewer controls
 *
 * `ownerBatchApi` is `batchCalls("owner")` — the identical server code with the
 * owner derived from the session, never from the path. Two differences, both of
 * them the server's:
 *
 *   - **No package picker.** `/batches/:id/packages` is registered only on the
 *     staff surface, because the pin is written at screening. Offering the
 *     control here would promise something that 403s on every click.
 *   - **Attribution is not asked for.** An upload is filed against the owner
 *     the session belongs to, never the one the metadata claims — which is the
 *     whole point of `BE-36` and the reason a supplier cannot mis-file.
 *
 * ## Scanning before starting, always
 *
 * The flow is create → upload → scan → read the findings → start. A supplier
 * who starts an unscanned package finds out what was wrong with it from a
 * transcoder error hours later, and the thing they most need to see — this file
 * has no metadata, this title is a duplicate — is exactly what the scan says.
 */

interface ShellContext {
  shell: OwnerShell;
}

/** A directory picker prefixes the chosen folder's own name; drop it. */
function relativePathOf(file: File): string {
  const withPath = file as File & { webkitRelativePath?: string };
  const raw = withPath.webkitRelativePath || file.name;
  const slash = raw.indexOf("/");
  return slash > 0 ? raw.slice(slash + 1) : raw;
}

export default function SupplierUploadPage() {
  const toast = useToast();
  const { t } = useTranslation(["supplier", "common"]);
  useOutletContext<ShellContext>();
  const [batches, setBatches] = useState<BatchView[] | null>(null);
  const [readiness, setReadiness] = useState<IngestReadiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [name, setName] = useState("");
  // The company's feeds; a delivery names one when there are several.
  const feeds = useOwnerFeeds(true);
  const [feedId, setFeedId] = useState("");
  /** The "New delivery" modal (name + Choose files / Choose folder). */
  const [newOpen, setNewOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  /** The single film being taken through the wizard, or null (`ART-9`). */
  const [wizardFile, setWizardFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await ownerBatchApi.listBatches();
      setBatches(res.batches);
      setReadiness(res.readiness);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * 🚨 Opens the picker SYNCHRONOUSLY, and creates nothing yet.
   *
   * The first build awaited `createBatch` and `load()` and only then called
   * `.click()` on the hidden input. A browser only honours a programmatic
   * click on a file input while the user activation from the real click is
   * still live, and an `await` ends it — so the dialog never opened, silently,
   * while an empty delivery appeared in the list below with a disabled "Send
   * to Nenda". Reported exactly that way: *"it just goes down to the rows
   * below, disabled button send to nenda"*.
   *
   * Nothing may be awaited between the operator's click and this call.
   */
  const pick = (which: "files" | "folder") => {
    setError(null);
    (which === "folder" ? folderRef : fileRef).current?.click();
  };

  /**
   * The delivery is created once there are files to put in it.
   *
   * Also the reason a cancelled dialog leaves nothing behind: an empty batch
   * per abandoned click is a list nobody can read a week later.
   */
  const upload = async (files: FileList) => {
    setError(null);
    const list = Array.from({ length: files.length }, (_, i) => files[i]);
    try {
      setBusy(t("supplierUploadPage.busy.creating"));
      const { batch } = await ownerBatchApi.createBatch({
        name: name.trim(),
        ...(feedId ? { feedId } : {}),
      });
      setName("");

      for (const [index, file] of list.entries()) {
        setBusy(
          t("supplierUploadPage.busy.uploading", {
            index: index + 1,
            total: list.length,
            name: file.name,
          })
        );
        // Sequential on purpose, matching the portal's own bulk client: a
        // package that takes an hour must not saturate a partner's line.
        const { uploadUrl } = await ownerBatchApi.batchUploadUrl(
          batch.id,
          relativePathOf(file)
        );
        await uploadInBlocks(uploadUrl, file, {
          contentType: file.type || "application/octet-stream",
        });
      }

      setBusy(t("supplierUploadPage.busy.checking"));
      await ownerBatchApi.scanBatch(batch.id);
      toast.success(
        t("supplierUploadPage.uploaded", {
          name: batch.name,
          count: list.length,
        })
      );
      await load();
    } catch (e) {
      const err = e as { code?: string; message: string };
      toast.error(describeError(t, "uploadDelivery", e));
      setError(
        err.code === "name_required" ||
          err.code === "storage_not_configured" ||
          err.code === "unsafe_path"
          ? t(`supplierUploadPage.errors.${err.code}`)
          : err.message
      );
      await load();
    } finally {
      setBusy(null);
    }
  };

  /**
   * Throw an unsent delivery away (`ART-7`).
   *
   * Not a confirmation dialog: a DRAFT delivery is staging, nothing in the
   * catalogue points at it, and the server refuses outright once it has been
   * started — so the destructive case this would guard against cannot be
   * reached from here. Guarding it anyway would be ceremony over an action
   * whose whole reason for existing is that somebody is stuck with a row they
   * cannot use.
   */
  const remove = async (batch: BatchView) => {
    setBusy(t("supplierUploadPage.busy.deleting"));
    setError(null);
    try {
      await ownerBatchApi.deleteBatch(batch.id);
      toast.success(t("supplierUploadPage.deleted", { name: batch.name }));
      await load();
    } catch (e) {
      const err = e as { code?: string; message: string };
      toast.error(describeError(t, "deleteDelivery", e, { name: batch.name }));
      setError(
        // On `blobs_not_removed` the row is still there ON PURPOSE — it is the
        // only handle left on the files that would not go — so the honest
        // instruction is "press it again", not a shrug.
        err.code === "batch_not_draft" || err.code === "blobs_not_removed"
          ? t(`supplierUploadPage.errors.${err.code}`)
          : err.message
      );
      await load();
    } finally {
      setBusy(null);
    }
  };

  const start = async (batch: BatchView) => {
    setBusy(t("supplierUploadPage.busy.sending"));
    setError(null);
    try {
      await ownerBatchApi.startBatch(batch.id);
      toast.success(t("supplierUploadPage.sent", { name: batch.name }));
      await load();
    } catch (e) {
      const err = e as { code?: string; message: string };
      toast.error(describeError(t, "sendDelivery", e, { name: batch.name }));
      setError(
        err.code === "nothing_to_start"
          ? t("supplierUploadPage.errors.nothing_to_start")
          : err.message
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack spacing={3}>
      {/* The wizard is a Dialog, so one film is guided IN PLACE — no
          navigation, and nothing to carry across a route change. */}
      <VodWizardDialog
        open={wizardFile !== null}
        surface="owner"
        initialFile={wizardFile}
        onClose={() => setWizardFile(null)}
        onFinished={() => {}}
      />

      <PageHeader
        eyebrow={t("supplierUploadPage.eyebrow")}
        title={t("supplierUploadPage.title")}
        subtitle={t("supplierUploadPage.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            disabled={busy !== null}
            onClick={() => {
              // Reset on open, never on close: clearing it in onClose would
              // empty the field while the modal is still fading out.
              setName("");
              setNewOpen(true);
            }}
            data-testid="batch-new"
          >
            {t("supplierUploadPage.newDelivery")}
          </Button>
        }
      />

      {error ? <Alert severity="error">{error}</Alert> : null}

      {/* The transcoder's own verdict, carried rather than guessed at. A
          delivery queued behind a dispatcher that cannot run is waiting
          differently from one that is simply next. */}
      {readiness && !readiness.ready ? (
        <Alert severity="warning">{t("supplierUploadPage.notReady")}</Alert>
      ) : null}

      {busy ? (
        <Box>
          <Typography variant="body2" sx={{ color: tokens.inkMuted, mb: 0.5 }}>
            {busy}
          </Typography>
          <LinearProgress />
        </Box>
      ) : null}

      {/* The hidden pickers live on the PAGE, not inside the modal: the modal
          closes as soon as files are chosen, and the upload carries on with
          its progress shown above. */}
      <input
        ref={fileRef}
        type="file"
        multiple
        hidden
        data-testid="batch-files"
        onChange={(e) => {
          const files = e.target.files;
          e.target.value = "";
          if (!files?.length) return;
          // Files are chosen: the modal's job is done. The upload (or the
          // wizard) carries on from here, with its progress on the page.
          setNewOpen(false);
          /* 🚨 ONE film is not a delivery (`ART-9`).
             The batch machinery earns its keep for a package with its own
             layout, sidecars, posters and subtitles. For a single file it
             strictly loses: no metadata step, no subtitles, no artwork, and
             the title's name guessed from a filename instead of asked for.
             So one file opens the wizard; everything else is a delivery. */
          if (files.length === 1) {
            setWizardFile(files[0]);
            return;
          }
          void upload(files);
        }}
      />
      <input
        ref={folderRef}
        type="file"
        multiple
        hidden
        data-testid="batch-folder"
        {...({
          webkitdirectory: "",
          directory: "",
        } as Record<string, string>)}
        onChange={(e) => {
          if (e.target.files?.length) {
            setNewOpen(false);
            void upload(e.target.files);
          }
          e.target.value = "";
        }}
      />

      <ItemFormSurface
        mode="create"
        open={newOpen}
        onClose={() => setNewOpen(false)}
        title={t("supplierUploadPage.newDelivery")}
        maxWidth="sm"
        data-testid="batch-new-dialog"
        actions={(dismiss) => (
          <>
            <Button onClick={() => dismiss()}>
              {t("common:buttons.cancel")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FolderIcon />}
              disabled={
                name.trim() === "" ||
                busy !== null ||
                !feedChoiceReady(feeds, feedId)
              }
              onClick={() => pick("folder")}
              data-testid="batch-create-folder"
            >
              {t("supplierUploadPage.chooseFolder")}
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              disabled={
                name.trim() === "" ||
                busy !== null ||
                !feedChoiceReady(feeds, feedId)
              }
              onClick={() => pick("files")}
              data-testid="batch-create"
            >
              {t("supplierUploadPage.chooseFiles")}
            </Button>
          </>
        )}
      >
        <TextField
          size="small"
          fullWidth
          autoFocus
          label={t("supplierUploadPage.nameLabel")}
          placeholder={t("supplierUploadPage.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1 }}
          inputProps={{ "data-testid": "batch-name" }}
        />
        <Box sx={{ mt: 2 }}>
          <OwnerFeedSelect feeds={feeds} value={feedId} onChange={setFeedId} />
        </Box>

        {/* 🚨 Two pickers, because the scanner genuinely accepts both.
            A bare video at the root is the FLAT layout — one title, its name
            taken from the filename — and metadata is optional throughout. A
            folder per title is the FOLDER layout, which is what you want when
            a title carries its own JSON, posters and subtitles.

            The first build offered only a directory picker, which imposed a
            constraint the system does not have: it made "I just want to send
            you this one film" impossible, and there was no way to discover
            that a single file would have worked. */}
        <Typography
          variant="caption"
          sx={{ color: tokens.inkFaint, display: "block", mt: 1 }}
        >
          {/* 🚨 The accepted formats, BEFORE the upload rather than after.
              Only `.mp4` is classified as a video (`PACKAGE_VIDEO_EXTENSIONS`),
              so a .mov test clip uploads perfectly and then scans to nothing —
              which is exactly how "0 titles" happens with no error in sight. */}
          <Trans
            i18nKey="supplierUploadPage.hint"
            t={t}
            components={{ strong: <strong /> }}
          />
        </Typography>
      </ItemFormSurface>

      {batches === null ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      ) : batches.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography sx={{ typography: "titleLg", mb: 1 }}>
            {t("supplierUploadPage.emptyTitle")}
          </Typography>
          <Typography
            sx={{ color: tokens.inkMuted, maxWidth: 460, mx: "auto" }}
          >
            {t("supplierUploadPage.emptyBody")}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {batches.map((batch) => (
            <BatchCard
              key={batch.id}
              batch={batch}
              busy={busy !== null}
              onStart={() => void start(batch)}
              onDelete={() => void remove(batch)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * One delivery, and the only question that matters about it: can it be sent,
 * and if not, what is wrong with which file.
 */
function BatchCard({
  batch,
  busy,
  onStart,
  onDelete,
}: {
  batch: BatchView;
  busy: boolean;
  onStart: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(["supplier", "common"]);
  const blocked = batch.items.filter((i) => i.blocked).length;
  /* 🚨 An ACCOUNT problem wearing a per-file costume.
     `supplier_not_bound` blocks every item in every delivery this account will
     ever make, and it is fixed by Nenda linking the account to a provider —
     not by the supplier touching their files. Repeating it once per row, in
     among genuine per-file findings, is how somebody spends an afternoon
     re-uploading a folder that was never the problem. */
  const notBound = batch.items.some((i) =>
    i.findings.some((f) => f.code === "supplier_not_bound")
  );
  const sent = batch.startedAt !== null;
  const scanned = batch.scannedAt !== null;

  /**
   * 🚨 Why "Send to Nenda" cannot be pressed — as a SENTENCE, not a boolean.
   *
   * The condition used to be `busy || !scanned || items.length === blocked`,
   * and that last term is `0 === 0` for a delivery whose scan found nothing.
   * So an empty package was permanently unsendable with no reason anywhere on
   * screen, which is exactly how this was reported: *"it uploaded and the file
   * ended up in the list but send to nenda is grayed, and I can't click it."*
   *
   * Two different situations were wearing the same disabled button:
   *
   *  - **nothing was found** — almost always a file the scanner does not
   *    classify as a video. Only `.mp4` is (`PACKAGE_VIDEO_EXTENSIONS`), so a
   *    `.mov`, an `.mkv` or a poster uploads perfectly and pairs to nothing.
   *  - **everything that was found is blocked** — the files are right, the
   *    findings below say what is wrong with them.
   *
   * The caption above the picker already warned about the first. That is the
   * wrong place: it is read before the mistake and forgotten by the time it
   * matters. The reason belongs beside the control that will not move.
   */
  const cannotSend = !scanned
    ? t("supplierUploadPage.cannotSend.notChecked")
    : batch.items.length === 0
      ? t("supplierUploadPage.cannotSend.nothingRecognised")
      : batch.items.length === blocked
        ? t("supplierUploadPage.cannotSend.allBlocked")
        : null;

  return (
    <Box
      sx={{
        background: tokens.surface,
        border: `1px solid ${tokens.divider}`,
        borderRadius: 2,
        p: 2.5,
      }}
      data-testid={`batch-${batch.id}`}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
            {batch.name}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
            {/* 🚨 "uploading" only while nothing has been checked AND nothing
                has been found. A delivery whose scan failed used to sit at
                "0 titles · uploading" for ever, which reads as work in
                progress — so nobody looks for the reason, and the reason was
                not on screen either (see packageFindings below). */}
            {t(
              sent
                ? "supplierUploadPage.summary.sent"
                : scanned
                  ? "supplierUploadPage.summary.checked"
                  : "supplierUploadPage.summary.waiting",
              { count: batch.items.length }
            )}
          </Typography>
        </Box>
        {!sent ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {/* A delivery that was never sent is the caller's to throw away —
                and the empty one they got from an unreadable file is the whole
                reason this exists. `cancel` is the verb for a STARTED batch
                and keeps the record; this removes it, bytes and all. */}
            <Button
              size="small"
              color="error"
              disabled={busy}
              onClick={onDelete}
              data-testid={`batch-delete-${batch.id}`}
            >
              {t("common:buttons.delete")}
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={busy || cannotSend !== null}
              onClick={onStart}
              data-testid={`batch-start-${batch.id}`}
            >
              {t("supplierUploadPage.sendToNenda")}
            </Button>
          </Stack>
        ) : (
          <Chip size="small" label={t("supplierUploadPage.sentChip")} />
        )}
      </Stack>

      {/* The reason, beside the control it explains. A disabled button with no
          sentence next to it is the defect this replaces. */}
      {!sent && cannotSend && (
        <Alert
          severity={batch.items.length === 0 ? "warning" : "info"}
          sx={{ mb: 1.5 }}
          data-testid={`batch-cannot-send-${batch.id}`}
        >
          {cannotSend}
        </Alert>
      )}

      {/* 🚨 The blocked count is the headline, not a detail in a row. A
          supplier who sends a delivery believing it complete, and learns
          three of twelve were dropped, learns it from a missing title on a
          television. */}
      {/* 🚨 The findings about the DELIVERY, not about a file in it.
          `no_video_files` and `package_empty` live here, and with zero items
          there is no row to hang them on — so a scan that found nothing
          rendered as a silent "0 titles" with its own explanation sitting
          unread on the wire. This is the first thing to draw, because when it
          fires there is nothing else on the card. */}
      {batch.packageFindings.length > 0 ? (
        <Alert
          severity={
            batch.packageFindings.some((f) => f.severity === "blocking")
              ? "error"
              : "info"
          }
          sx={{ mb: 1.5 }}
          data-testid={`batch-findings-${batch.id}`}
        >
          <Stack spacing={0.25}>
            {batch.packageFindings.map((f, i) => (
              <Typography variant="bodySm" key={`${f.code}-${i}`}>
                {packageFindingText(t, f)}
              </Typography>
            ))}
          </Stack>
        </Alert>
      ) : null}

      {notBound ? (
        <Alert severity="error" sx={{ mb: 1.5 }} data-testid="not-bound">
          {t("supplierUploadPage.notBound")}
        </Alert>
      ) : blocked > 0 ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {t("supplierUploadPage.blocked", {
            blocked,
            total: batch.items.length,
          })}
        </Alert>
      ) : null}

      {batch.items.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: tokens.inkMuted }}>
                  {t("common:labels.title")}
                </TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>
                  {t("common:labels.file")}
                </TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>
                  {t("common:labels.state")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batch.items.map((item) => (
                <TableRow key={item.key}>
                  <TableCell>{item.title || item.key}</TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "fontFamilyMono",
                      typography: "meta",
                      color: tokens.inkFaint,
                    }}
                  >
                    {item.videoPath}
                  </TableCell>
                  <TableCell>
                    {item.blocked ? (
                      <Stack spacing={0.25}>
                        <Chip
                          size="small"
                          label={t("supplierUploadPage.cannotBeSent")}
                          color="error"
                          sx={{ height: 20, typography: "micro" }}
                        />
                        {/* The reason, in the supplier's own words rather than
                            a code they would have to ask us about. */}
                        {item.findings.slice(0, 2).map((f, i) => (
                          <Typography
                            key={i}
                            variant="caption"
                            sx={{ color: tokens.inkFaint }}
                          >
                            {packageFindingText(t, f)}
                          </Typography>
                        ))}
                      </Stack>
                    ) : item.jobError ? (
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.error }}
                      >
                        {item.jobError}
                      </Typography>
                    ) : item.phase ? (
                      <Chip
                        size="small"
                        label={item.phase}
                        sx={{ height: 20, typography: "micro" }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={t("supplierUploadPage.ready")}
                        sx={{ height: 20, typography: "micro" }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
}
